import { v4 as uuidv4 } from 'uuid';
import { 
  getCurrentSession as getAppCurrentSession, 
  createNewSession as createAppNewSession, 
  saveCurrentSession as saveAppCurrentSession 
} from './sessionUtils';

/**
 * NEW TOOL TRACKING SYSTEM
 * Simple session-based tracking with 30-second confirmation delay
 */


// Get current tool tracking object
function getCurrentTool() {
  const raw = localStorage.getItem('currentTool');
  return raw ? JSON.parse(raw) : null;
}

// Save current tool tracking object
function saveCurrentTool(obj) {
  localStorage.setItem('currentTool', JSON.stringify(obj));
}

// Get user events array
function getUserEvents() {
  const raw = localStorage.getItem('userEvents');
  return raw ? JSON.parse(raw) : [];
}

// Save user events array
function saveUserEvents(events) {
  localStorage.setItem('userEvents', JSON.stringify(events));
}

// Add a session to userEvents
function addUserEvent(session) {
  const events = getUserEvents();
  events.push(session);
  saveUserEvents(events);
}

/**
 * SPACES CONFIGURATION
 */
export const SPACES = {
  PROJECT_SPACE: 'project_space',
  PERSONAL_SPACE: 'personal_space',
  MANAGER_SPACE: 'manager_space'
};



/**
 * Start a new tool tracking session (creates currentTool)
 * Utilise le sessionId de la session courante (currentSession) s'il existe
 * @param {string} userEmail - User email
 */
export function startTrackingSession(userEmail) {
  // Only create a new session if one does not already exist
  const existing = getCurrentTool();
  if (existing) {
    // Align with app sessionId if mismatched
    try {
      const appSession = getAppCurrentSession();
      if (appSession?.sessionId && existing.sessionId !== appSession.sessionId) {
        existing.sessionId = appSession.sessionId;
        saveCurrentTool(existing);
      }
    } catch {}
    return existing;
  }
  // Ensure there is a currentUserSession in the app, create one if missing/invalid
  let appSession = getAppCurrentSession();
  if (!appSession || appSession.endedAt || (userEmail && appSession.userId !== userEmail)) {
    try {
      appSession = createAppNewSession(userEmail);
      saveAppCurrentSession(appSession);
    } catch (e) {
      // Fallback (shouldn't happen): generate a temporary id
      appSession = { sessionId: uuidv4(), userId: userEmail };
    }
  }
  const toolSession = {
    sessionId: appSession.sessionId,
    datetime: new Date().toISOString(),
    userEmail: userEmail,
    projectTools: [],
    personalTools: [],
    managerTools: []
  };
  saveCurrentTool(toolSession);
  return toolSession;
}


/**
 * End current tool tracking session: move currentTool to userEvents
 */
export function endTrackingSession() {
  const currentTool = getCurrentTool();
  if (!currentTool) return null;
  // Cancel any pending timers
  Object.keys(pendingToolTimers).forEach(timerKey => {
    clearTimeout(pendingToolTimers[timerKey]);
    delete pendingToolTimers[timerKey];
  });
  currentTool.endDatetime = new Date().toISOString();
  addUserEvent(currentTool);
  localStorage.removeItem('currentTool');
  return currentTool;
}


// Timers for each space
let pendingToolTimers = {};
// Keep the last pending tool info per space so we can flush on demand
let pendingToolInfo = {};


/**
 * Add tool to currentTool with 10-second delay. If tool changes before 10s, do not log.
 * @param {string} toolName - Name of the tool
 * @param {string} space - Space (project_space, personal_space, manager_space)
 */
export function trackTool(toolName, space) {
  try {
    const currentTool = getCurrentTool();
    if (!currentTool) {
      console.warn('⚠️ No active tracking session. Please start a session first.');
      return;
    }
    let toolsArray;
    switch (space) {
      case SPACES.PROJECT_SPACE:
        toolsArray = 'projectTools';
        break;
      case SPACES.PERSONAL_SPACE:
        toolsArray = 'personalTools';
        break;
      case SPACES.MANAGER_SPACE:
        toolsArray = 'managerTools';
        break;
      default:
        console.warn('⚠️ Unknown space:', space);
        return;
    }
    // Cancel any pending timer for this space (user switched tools)
    if (pendingToolTimers[space]) {
      clearTimeout(pendingToolTimers[space]);
      delete pendingToolTimers[space];
    }
    // Set 10-second delay before confirming tool usage (allow multiple logs)
  pendingToolInfo[space] = { toolName, requestedAt: new Date().toISOString(), toolsArray };
    pendingToolTimers[space] = setTimeout(() => {
      const session = getCurrentTool();
      if (session && session.sessionId === currentTool.sessionId) {
        // Store only the tool name as a string
        session[toolsArray].push(toolName);
        saveCurrentTool(session);
        // Remove personalTools from currentUserSession if it exists
        let currentSessionRaw = localStorage.getItem('currentUserSession');
        if (currentSessionRaw) {
          try {
            let currentSession = JSON.parse(currentSessionRaw);
            if (currentSession.personalTools) {
              delete currentSession.personalTools;
              localStorage.setItem('currentUserSession', JSON.stringify(currentSession));
            }
          } catch (e) {}
        }
        console.log(`✅ Logged ${toolName} to ${space} tools after 10s delay`);
      }
  delete pendingToolTimers[space];
  delete pendingToolInfo[space];
    }, 10000);
    console.log(`⏳ Will log ${toolName} to ${space} tools in 10 seconds...`);
  } catch (error) {
    console.error('❌ Error in trackTool:', error);
  }
}


// Not needed anymore, startTrackingSession always resets


/**
 * Get current tool tracking info
 */
export function getCurrentSessionInfo() {
  return getCurrentTool();
}


/**
 * Get all user tool tracking sessions
 */
export function getAllSessions() {
  const completed = getUserEvents();
  const current = getCurrentTool();
  return {
    completedSessions: completed,
    currentSession: current,
    totalSessions: completed.length + (current ? 1 : 0)
  };
}


/**
 * Clear all tool tracking data (for debugging)
 */
export function clearAllTrackingData() {
  localStorage.removeItem('currentTool');
  localStorage.removeItem('userEvents');
  console.log('🧹 All tool tracking data cleared');
}

/**
 * Force-confirm all pending tools immediately (used on logout)
 */
export function confirmAllPendingToolsNow() {
  try {
    const session = getCurrentTool();
    if (!session) return;
    Object.keys(pendingToolTimers).forEach(space => {
      try { clearTimeout(pendingToolTimers[space]); } catch {}
      const info = pendingToolInfo[space];
      if (info && info.toolsArray) {
        session[info.toolsArray] = session[info.toolsArray] || [];
  // Store only the tool name as a string
  session[info.toolsArray].push(info.toolName);
      }
      delete pendingToolTimers[space];
      delete pendingToolInfo[space];
    });
    saveCurrentTool(session);
  const pc = session.projectTools?.length || 0;
  const pe = session.personalTools?.length || 0;
  const mc = session.managerTools?.length || 0;
  console.log(`✅ Forced confirmation of pending tools before sync (project=${pc}, personal=${pe}, manager=${mc})`);
  } catch (e) {
    // non-fatal
  }
}


/**
 * Get tracking statistics
 */
export function getTrackingStats() {
  const { completedSessions, currentSession } = getAllSessions();
  const allSessions = [...completedSessions];
  if (currentSession) allSessions.push(currentSession);
  const stats = {
    totalSessions: allSessions.length,
    totalProjectTools: 0,
    totalPersonalTools: 0,
    totalManagerTools: 0,
    uniqueProjectTools: new Set(),
    uniquePersonalTools: new Set(),
    uniqueManagerTools: new Set()
  };
  allSessions.forEach(session => {
    stats.totalProjectTools += session.projectTools.length;
    stats.totalPersonalTools += session.personalTools.length;
    stats.totalManagerTools += session.managerTools.length;
    session.projectTools.forEach(tool => stats.uniqueProjectTools.add(tool));
    session.personalTools.forEach(tool => stats.uniquePersonalTools.add(tool));
    session.managerTools.forEach(tool => stats.uniqueManagerTools.add(tool));
  });
  return {
    ...stats,
    uniqueProjectTools: Array.from(stats.uniqueProjectTools),
    uniquePersonalTools: Array.from(stats.uniquePersonalTools),
    uniqueManagerTools: Array.from(stats.uniqueManagerTools)
  };
}


/**
 * Handle user change (different user logs in)
 * Saves current tool and starts new one for new user
 * @param {string} newUserEmail - New user email
 */
export function handleUserChange(newUserEmail) {
  try {
    const currentTool = getCurrentTool();
    if (currentTool && currentTool.userEmail !== newUserEmail) {
      endTrackingSession();
      return startTrackingSession(newUserEmail);
    }
    // Same user, just ensure session exists
    return startTrackingSession(newUserEmail);
  } catch (error) {
    console.error('❌ Error in handleUserChange:', error);
    try {
      return startTrackingSession(newUserEmail);
    } catch (fallbackError) {
      console.error('❌ Fallback also failed:', fallbackError);
      return null;
    }
  }
}